import {
  ApplyConditionPayload,
  IDentalData,
} from '@dataTypes/chartingFeature.types'
import { normalizeCondition } from '@utils/charting.utils'
import {
  addConditionToDentalData,
  getConditionsByType,
  getDentalDataForTooth,
  hasCondition,
  removeConditionFromDentalData,
  removeConditionsStartingWith,
} from '@utils/dental-data.utils'

type ApplyConditionPayloadsToDentalDataParams = {
  dentalData: IDentalData[]
  selectedTooth: number
  conditionOrConditions: ApplyConditionPayload | ApplyConditionPayload[]
}

const SURFACE_REQUIRED_CONDITIONS = new Set<string>([
  'caries',
  'recurring_caries',
  'filling',
  'erosion',
  'inlay',
  'onlay',
  'drifted',
  'crack',
  'abrasion',
  'attrition',
  'recession',
])

const BASE_PRESERVED_TYPE_CONDITIONS = new Set<string>([
  'crack',
  'abrasion',
  'attrition',
  'recession',
  'furcation',
])

const EXCLUSIVE_TYPE_CONDITIONS = new Set<string>([
  'furcation',
  'periradicular',
  'impacted',
  'recession',
  'attrition',
  'abrasion',
])

const MATERIAL_GROUPS: Record<string, string[]> = {
  filling: ['ceramic', 'composite', 'gold', 'amalgam'],
  inlay: ['gold', 'zirconia', 'ceramic'],
  onlay: ['gold', 'zirconia', 'ceramic'],
  crown: ['zirconia', 'pfm', 'pfz', 'metal', 'ceramic'],
  bridge: ['zirconia', 'pfm', 'pfz', 'metal', 'ceramic'],
  veneer: ['ceramic', 'composite', 'zirconia'],
}

const SUPPORT_TYPES = ['left_abutment', 'right_abutment', 'right_left_abutment']

const normalizeApplyConditionPayload = (
  payload: ApplyConditionPayload,
): ApplyConditionPayload => ({
  condition: normalizeCondition(payload.condition),
  type: payload.type?.toLowerCase() ?? null,
  material: payload.material?.toLowerCase() ?? null,
  surface: payload.surface?.toLowerCase() ?? null,
})

const findConditionBeingRemoved = (
  dentalData: IDentalData[],
  selectedTooth: number,
  payload: ApplyConditionPayload,
): IDentalData | undefined => {
  return dentalData.find((data) => {
    const dataToothNumber =
      typeof data.tooth_number === 'string'
        ? data.tooth_number
        : String(data.tooth_number)

    return (
      dataToothNumber === selectedTooth.toString() &&
      data.condition === payload.condition &&
      data.surface === payload.surface &&
      (payload.type === null || data.type === payload.type) &&
      (payload.material === null || data.material === payload.material)
    )
  })
}

const preserveBaseConditionAfterTypeRemoval = (
  dentalData: IDentalData[],
  selectedTooth: number,
  condition: string,
): IDentalData[] => {
  const toothData = getDentalDataForTooth(dentalData, selectedTooth)
  const remaining = toothData.filter((item) => item.condition === condition)

  if (remaining.length > 0) {
    return dentalData
  }

  return addConditionToDentalData(
    dentalData,
    selectedTooth,
    condition,
    null,
    null,
    null,
    false,
  )
}

const removeAppliedCondition = (
  dentalData: IDentalData[],
  selectedTooth: number,
  payload: ApplyConditionPayload,
): IDentalData[] => {
  let updatedDentalData = removeConditionFromDentalData(
    dentalData,
    selectedTooth,
    payload.condition,
    payload.type,
    payload.material,
    payload.surface,
  )

  const conditionBeingRemoved = findConditionBeingRemoved(
    dentalData,
    selectedTooth,
    payload,
  )

  if (payload.condition === 'filling' && payload.surface) {
    const materialToPreserve =
      conditionBeingRemoved?.material ?? payload.material

    if (materialToPreserve) {
      const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
      const remainingFilling = toothData.filter(
        (item) => item.condition === 'filling',
      )
      const remainingSurfaces = remainingFilling.filter(
        (item) => item.surface !== null,
      )

      if (remainingSurfaces.length === 0) {
        const materialOnlyExists = remainingFilling.some(
          (item) =>
            item.material === materialToPreserve && item.surface === null,
        )

        if (!materialOnlyExists) {
          updatedDentalData = addConditionToDentalData(
            updatedDentalData,
            selectedTooth,
            'filling',
            null,
            materialToPreserve,
            null,
            false,
          )
        }
      }
    }
  }

  if (
    (payload.condition === 'caries' ||
      payload.condition === 'recurring_caries') &&
    payload.surface
  ) {
    const typeToPreserve = conditionBeingRemoved?.type ?? payload.type

    if (typeToPreserve) {
      const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
      const remainingCaries = toothData.filter(
        (item) => item.condition === payload.condition,
      )
      const remainingSurfaces = remainingCaries.filter(
        (item) => item.surface !== null,
      )

      if (remainingSurfaces.length === 0) {
        const typeOnlyExists = remainingCaries.some(
          (item) => item.type === typeToPreserve && item.surface === null,
        )

        if (!typeOnlyExists) {
          updatedDentalData = addConditionToDentalData(
            updatedDentalData,
            selectedTooth,
            payload.condition,
            typeToPreserve,
            null,
            null,
            false,
          )
        }
      }
    }
  }

  if (
    (payload.condition === 'inlay' || payload.condition === 'onlay') &&
    payload.surface
  ) {
    const materialToPreserve =
      conditionBeingRemoved?.material ?? payload.material

    if (materialToPreserve) {
      const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
      const remainingRestorative = toothData.filter(
        (item) => item.condition === payload.condition,
      )
      const remainingSurfaces = remainingRestorative.filter(
        (item) => item.surface !== null,
      )

      if (remainingSurfaces.length === 0) {
        const materialOnlyExists = remainingRestorative.some(
          (item) =>
            item.material === materialToPreserve && item.surface === null,
        )

        if (!materialOnlyExists) {
          updatedDentalData = addConditionToDentalData(
            updatedDentalData,
            selectedTooth,
            payload.condition,
            null,
            materialToPreserve,
            null,
            false,
          )
        }
      }
    }
  }

  if (payload.type && !payload.surface) {
    if (BASE_PRESERVED_TYPE_CONDITIONS.has(payload.condition)) {
      updatedDentalData = preserveBaseConditionAfterTypeRemoval(
        updatedDentalData,
        selectedTooth,
        payload.condition,
      )
    }
  }

  if (
    payload.surface &&
    payload.condition !== 'filling' &&
    payload.condition !== 'caries' &&
    payload.condition !== 'recurring_caries' &&
    payload.condition !== 'inlay' &&
    payload.condition !== 'onlay'
  ) {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const remainingConditionRows = toothData.filter(
      (item) => item.condition === payload.condition,
    )
    const remainingSurfaces = remainingConditionRows.filter(
      (item) => item.surface !== null,
    )
    const hasBaseRow = remainingConditionRows.some(
      (item) => item.surface === null,
    )

    if (remainingSurfaces.length === 0 && !hasBaseRow) {
      updatedDentalData = addConditionToDentalData(
        updatedDentalData,
        selectedTooth,
        payload.condition,
        conditionBeingRemoved?.type ?? payload.type,
        conditionBeingRemoved?.material ?? payload.material,
        null,
        conditionBeingRemoved?.recurrent ?? false,
      )
    }
  }

  if (!payload.type && !payload.material && !payload.surface) {
    updatedDentalData = removeConditionsStartingWith(
      updatedDentalData,
      selectedTooth,
      payload.condition,
    )
  }

  return updatedDentalData
}

const addAppliedCondition = (
  dentalData: IDentalData[],
  selectedTooth: number,
  payload: ApplyConditionPayload,
): IDentalData[] => {
  let updatedDentalData = dentalData

  const toothDataBeforeAdd = getDentalDataForTooth(
    updatedDentalData,
    selectedTooth,
  )
  const conditionsToCleanup = new Set<string>()

  toothDataBeforeAdd.forEach((item) => {
    if (
      SURFACE_REQUIRED_CONDITIONS.has(item.condition) &&
      item.condition !== payload.condition
    ) {
      conditionsToCleanup.add(item.condition)
    }
  })

  conditionsToCleanup.forEach((conditionName) => {
    const rowsForCondition = getDentalDataForTooth(
      updatedDentalData,
      selectedTooth,
    ).filter((item) => item.condition === conditionName)

    const hasAnySurface = rowsForCondition.some((item) => item.surface !== null)
    const hasOnlyBaseRows = rowsForCondition.every(
      (item) =>
        item.type === null && item.material === null && item.surface === null,
    )
    const shouldRemoveOnSwitch = BASE_PRESERVED_TYPE_CONDITIONS.has(
      conditionName,
    )
      ? hasOnlyBaseRows
      : !hasAnySurface

    if (shouldRemoveOnSwitch) {
      updatedDentalData = removeConditionsStartingWith(
        updatedDentalData,
        selectedTooth,
        conditionName,
      )
    }
  })

  let preservedInlayOnlaySurfaces: string[] = []
  let preservedFillingSurfaces: string[] = []
  if (payload.condition === 'filling' && payload.material && !payload.surface) {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existing = toothData.filter((item) => item.condition === 'filling')
    preservedFillingSurfaces = existing
      .filter((item) => item.surface !== null)
      .map((item) => item.surface)
      .filter((surface): surface is string => surface !== null)
  }

  if (
    ['inlay', 'onlay'].includes(payload.condition) &&
    payload.material &&
    !payload.surface
  ) {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existing = toothData.filter(
      (item) => item.condition === payload.condition,
    )
    preservedInlayOnlaySurfaces = existing
      .filter((item) => item.surface !== null)
      .map((item) => item.surface)
      .filter((surface): surface is string => surface !== null)
  }

  if (
    payload.material &&
    MATERIAL_GROUPS[payload.condition] &&
    !payload.surface
  ) {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existingMaterialEntries = toothData.filter(
      (item) =>
        item.condition === payload.condition &&
        item.material !== null &&
        item.material !== payload.material,
    )

    existingMaterialEntries.forEach((entry) => {
      updatedDentalData = removeConditionFromDentalData(
        updatedDentalData,
        selectedTooth,
        payload.condition,
        null,
        entry.material,
        entry.surface,
      )
    })
  }

  let preservedRecurrent = false
  if (payload.condition === 'caries' && payload.type) {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existingCondition = toothData.find(
      (item) => item.condition === payload.condition,
    )

    if (existingCondition && existingCondition.recurrent) {
      preservedRecurrent = true
    }
  }

  let preservedAbrasionSurfaces: string[] = []
  if (payload.condition === 'abrasion' && payload.surface && !payload.type) {
    const existingAbrasion = getDentalDataForTooth(
      updatedDentalData,
      selectedTooth,
    ).find((item) => item.condition === 'abrasion' && item.type)
    if (existingAbrasion?.type) {
      payload.type = existingAbrasion.type
    }
  }

  if (payload.condition === 'abrasion' && payload.type && !payload.surface) {
    preservedAbrasionSurfaces = getDentalDataForTooth(
      updatedDentalData,
      selectedTooth,
    )
      .filter((item) => item.condition === 'abrasion' && item.surface)
      .map((item) => item.surface)
      .filter((surface): surface is string => surface !== null)
  }

  if (
    EXCLUSIVE_TYPE_CONDITIONS.has(payload.condition) &&
    payload.type &&
    !payload.surface
  ) {
    updatedDentalData = removeConditionsStartingWith(
      updatedDentalData,
      selectedTooth,
      payload.condition,
    )
  }

  if (
    payload.type &&
    (payload.condition === 'abutment' ||
      (payload.condition === 'bridge' && payload.type.endsWith('_abutment')))
  ) {
    const supportCondition = 'abutment'
    updatedDentalData = removeConditionFromDentalData(
      updatedDentalData,
      selectedTooth,
      supportCondition,
      null,
      null,
      null,
    )

    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const bridgeData = getConditionsByType(toothData, supportCondition)

    bridgeData
      .filter((item) => item.type === null && item.material !== null)
      .forEach((item) => {
        updatedDentalData = removeConditionFromDentalData(
          updatedDentalData,
          selectedTooth,
          'bridge',
          null,
          item.material,
          null,
        )
      })

    SUPPORT_TYPES.forEach((supportType) => {
      if (supportType !== payload.type) {
        bridgeData
          .filter((item) => item.type === supportType)
          .forEach((item) => {
            updatedDentalData = removeConditionFromDentalData(
              updatedDentalData,
              selectedTooth,
              supportCondition,
              supportType,
              item.material,
              null,
            )
          })
        return
      }

      if (payload.material) {
        bridgeData
          .filter(
            (item) =>
              item.type === supportType && item.material !== payload.material,
          )
          .forEach((item) => {
            updatedDentalData = removeConditionFromDentalData(
              updatedDentalData,
              selectedTooth,
              supportCondition,
              supportType,
              item.material,
              null,
            )
          })
      }
    })
  }

  if (
    (payload.condition === 'caries' ||
      payload.condition === 'recurring_caries') &&
    payload.type &&
    !payload.surface
  ) {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existingSurfaces = toothData
      .filter(
        (item) =>
          item.condition === payload.condition &&
          item.surface &&
          item.type &&
          item.type !== payload.type,
      )
      .map((item) => item.surface)
      .filter((surface): surface is string => surface !== null)

    updatedDentalData = removeConditionsStartingWith(
      updatedDentalData,
      selectedTooth,
      payload.condition,
    )

    existingSurfaces.forEach((surface) => {
      updatedDentalData = addConditionToDentalData(
        updatedDentalData,
        selectedTooth,
        payload.condition,
        payload.type,
        null,
        surface,
        preservedRecurrent,
      )
    })
  }

  let shouldSkipAddingCondition = false
  if (
    payload.condition === 'abrasion' &&
    payload.type &&
    !payload.surface &&
    preservedAbrasionSurfaces.length > 0
  ) {
    preservedAbrasionSurfaces.forEach((surface) => {
      updatedDentalData = addConditionToDentalData(
        updatedDentalData,
        selectedTooth,
        'abrasion',
        payload.type,
        null,
        surface,
        false,
      )
    })
    shouldSkipAddingCondition = true
  }
  if (payload.condition === 'filling') {
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existingFilling = toothData.filter(
      (item) => item.condition === 'filling',
    )

    if (payload.surface && !payload.material) {
      const materialOnlyFilling = existingFilling.find(
        (item) => item.material !== null && item.surface === null,
      )

      if (materialOnlyFilling?.material) {
        payload.material = materialOnlyFilling.material
      } else {
        const surfaceWithMaterial = existingFilling.find(
          (item) => item.surface !== null && item.material !== null,
        )
        if (surfaceWithMaterial?.material) {
          payload.material = surfaceWithMaterial.material
        }
      }

      existingFilling
        .filter((item) => item.material !== null && item.surface === null)
        .forEach((item) => {
          updatedDentalData = removeConditionFromDentalData(
            updatedDentalData,
            selectedTooth,
            'filling',
            null,
            item.material,
            null,
          )
        })
    }

    if (payload.material && !payload.surface) {
      const existingSurfaces =
        preservedFillingSurfaces.length > 0
          ? preservedFillingSurfaces
          : existingFilling
              .filter((item) => item.surface !== null)
              .map((item) => item.surface)
              .filter((surface): surface is string => surface !== null)

      updatedDentalData = updatedDentalData.filter((item) => {
        const dataToothNumber =
          typeof item.tooth_number === 'string'
            ? item.tooth_number
            : String(item.tooth_number)

        return (
          dataToothNumber !== selectedTooth.toString() ||
          item.condition !== 'filling'
        )
      })

      if (existingSurfaces.length > 0) {
        existingSurfaces.forEach((surface) => {
          updatedDentalData = addConditionToDentalData(
            updatedDentalData,
            selectedTooth,
            'filling',
            null,
            payload.material,
            surface,
            false,
          )
        })
        shouldSkipAddingCondition = true
      }
    }
  }

  if (['inlay', 'onlay'].includes(payload.condition)) {
    const restorativeCondition = payload.condition
    const toothData = getDentalDataForTooth(updatedDentalData, selectedTooth)
    const existingRestorative = toothData.filter(
      (item) => item.condition === restorativeCondition,
    )

    if (payload.surface && !payload.material) {
      const materialOnlyCondition = existingRestorative.find(
        (item) => item.material !== null && item.surface === null,
      )

      if (materialOnlyCondition?.material) {
        payload.material = materialOnlyCondition.material
      } else {
        const surfaceWithMaterial = existingRestorative.find(
          (item) => item.surface !== null && item.material !== null,
        )
        if (surfaceWithMaterial?.material) {
          payload.material = surfaceWithMaterial.material
        }
      }

      existingRestorative
        .filter((item) => item.material !== null && item.surface === null)
        .forEach((item) => {
          updatedDentalData = removeConditionFromDentalData(
            updatedDentalData,
            selectedTooth,
            restorativeCondition,
            null,
            item.material,
            null,
          )
        })
    }

    if (payload.material && !payload.surface) {
      const existingSurfaces =
        preservedInlayOnlaySurfaces.length > 0
          ? preservedInlayOnlaySurfaces
          : existingRestorative
              .filter((item) => item.surface !== null)
              .map((item) => item.surface)
              .filter((surface): surface is string => surface !== null)

      updatedDentalData = updatedDentalData.filter((item) => {
        const dataToothNumber =
          typeof item.tooth_number === 'string'
            ? item.tooth_number
            : String(item.tooth_number)

        return (
          dataToothNumber !== selectedTooth.toString() ||
          item.condition !== restorativeCondition
        )
      })

      if (existingSurfaces.length > 0) {
        existingSurfaces.forEach((surface) => {
          updatedDentalData = addConditionToDentalData(
            updatedDentalData,
            selectedTooth,
            restorativeCondition,
            null,
            payload.material,
            surface,
            false,
          )
        })
        shouldSkipAddingCondition = true
      }
    }
  }

  if (!shouldSkipAddingCondition) {
    const recurrentValue =
      payload.condition === 'caries' ? preservedRecurrent : false
    updatedDentalData = addConditionToDentalData(
      updatedDentalData,
      selectedTooth,
      payload.condition,
      payload.type,
      payload.material,
      payload.surface,
      recurrentValue,
    )
  }

  return updatedDentalData
}

const applySingleConditionPayload = (
  dentalData: IDentalData[],
  selectedTooth: number,
  payload: ApplyConditionPayload,
): IDentalData[] => {
  const isMaterialSetAction =
    payload.material !== null &&
    payload.surface === null &&
    ['filling', 'inlay', 'onlay'].includes(payload.condition)
  if (isMaterialSetAction) {
    return addAppliedCondition(dentalData, selectedTooth, payload)
  }

  const conditionExists = hasCondition(
    dentalData,
    selectedTooth,
    payload.condition,
    payload.type,
    payload.material,
    payload.surface,
  )

  if (conditionExists) {
    return removeAppliedCondition(dentalData, selectedTooth, payload)
  }

  return addAppliedCondition(dentalData, selectedTooth, payload)
}

export const applyConditionPayloadsToDentalData = ({
  dentalData,
  selectedTooth,
  conditionOrConditions,
}: ApplyConditionPayloadsToDentalDataParams): IDentalData[] => {
  const conditions: ApplyConditionPayload[] = Array.isArray(
    conditionOrConditions,
  )
    ? conditionOrConditions
    : [conditionOrConditions]

  const selectedToothNumber = selectedTooth.toString()
  const targetToothRows = dentalData
    .filter((row) => {
      return String(row.tooth_number) === selectedToothNumber
    })
    .map((row) => {
      if (typeof row.tooth_number === 'string') {
        return row
      }
      return {
        ...row,
        tooth_number: String(row.tooth_number),
      }
    })
  const unchangedRows = dentalData.filter((row) => {
    return String(row.tooth_number) !== selectedToothNumber
  })

  const updatedTargetRows = conditions.reduce<IDentalData[]>(
    (currentData, conditionPayload) => {
      const normalizedPayload = normalizeApplyConditionPayload(conditionPayload)
      return applySingleConditionPayload(
        currentData,
        selectedTooth,
        normalizedPayload,
      )
    },
    targetToothRows,
  )

  return [...unchangedRows, ...updatedTargetRows]
}
